
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export function useTestCall() {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const goToIntegrations = () => {
    navigate('/integrations?tab=twilio');
  };

  // Check if Twilio is configured
  const checkTwilioConfiguration = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('twilio-integration', {
        body: { action: 'checkConfig' }
      });

      if (error || !data?.configured) {
        return false;
      }
      return true;
    } catch (error) {
      console.warn("Error checking Twilio configuration:", error);
      return false;
    }
  };

  const initiateTestCall = async () => {
    if (!phoneNumber) {
      toast.error("Please enter a phone number");
      return;
    }

    // Basic validation - check if it's at least 10 digits
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    if (digitsOnly.length < 10) {
      toast.error("Please enter at least 10 digits for your phone number");
      return;
    }

    setIsLoading(true);
    try {
      // First check if Twilio is properly configured
      const isConfigured = await checkTwilioConfiguration();
      
      if (!isConfigured) {
        toast.error("Twilio credentials are not configured. Please set them up in Integrations.", {
          action: {
            label: "Configure Twilio",
            onClick: goToIntegrations
          }
        });
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke('twilio-integration', {
        body: {
          action: 'initiateCall',
          candidateName: 'Test User',
          candidatePhone: digitsOnly,
          jobTitle: 'Software Developer',
          scriptText: "This is a test call from your AI recruiting assistant. Thank you for testing our system. Have a great day!"
        }
      });

      if (error) throw error;
      
      if (data?.error === "Twilio credentials are not configured") {
        toast.error("Twilio credentials are not configured. Please set them up in Integrations.", {
          action: {
            label: "Configure Twilio",
            onClick: goToIntegrations
          }
        });
        return;
      }

      toast.success("Test call initiated successfully!");
      console.log("Call response:", data);
    } catch (error) {
      console.error("Error initiating test call:", error);
      
      // Check if the error is related to credentials
      const errorMsg = error instanceof Error ? error.message : String(error);
      if (errorMsg.includes("credentials") || errorMsg.includes("Edge Function")) {
        toast.error("Twilio integration error. Please configure your Twilio credentials.", {
          action: {
            label: "Configure Twilio",
            onClick: goToIntegrations
          }
        });
      } else {
        toast.error("Failed to initiate test call");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    phoneNumber,
    setPhoneNumber,
    isLoading,
    initiateTestCall
  };
}
